import React, { useState, useMemo, useCallback } from "react";
import { FiEdit2, FiTrash2, FiDownload } from "react-icons/fi";
import { TableColumnFilter } from "newui/components/Filters/TableColumnFilter";
import type { FilterOption } from "newui/components/Filters/TableColumnFilter";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";
import type { KeyPersonnelResume, DisciplineGroup } from "../types/KeyPersonnel.type";
import { ColumnSortHeader, SortDirection } from "newui/components/TableComponents";
import FallbackImage from "components/FallbackImage/FallbackImage";

type TableMode = "manage" | "profile-view";
type KeyPersonnelSortField =
	| "full_name"
	| "position"
	| "experience"
	| "projects"
	| "resume";

interface KeyPersonnelTableItem {
	id: number;
	avatar_location: string | null;
	disciplines?: {
		id: number;
		expertise?: { id: number; name: string };
		name: string;
	}[];
	disciplinesFormatted?: DisciplineGroup[];
	experience: number;
	first_name: string;
	full_name: string;
	last_name: string;
	position: string;
	projects_completed?: { name: string }[];
	resume: KeyPersonnelResume[];
	sectors: { id: number; name: string }[];
}

interface KeyPersonnelTableProps {
	forceShowSector?: boolean;
	mode?: TableMode;
	personnel: KeyPersonnelTableItem[];
	profileTypeId?: number;
	sectorFilter?: number[];
	/**
	 * When provided, sector filtering is delegated to the caller (server-driven).
	 * All three must be supplied together. Local sector filtering is skipped.
	 */
	sectorOptions?: FilterOption<number>[];
	showDragColumn?: boolean;
	onDelete?: (id: number) => void;
	onEdit?: (id: number) => void;
	onRowClick?: (person: KeyPersonnelTableItem) => void;
	onSectorFilterChange?: (ids: number[]) => void;
	renderRow?: (
		person: KeyPersonnelTableItem,
		cells: React.ReactNode,
	) => React.ReactNode;
}

const KeyPersonnelTable: React.FC<KeyPersonnelTableProps> = ({
	personnel,
	mode = "manage",
	profileTypeId = 2,
	onEdit,
	onDelete,
	onRowClick,
	showDragColumn = false,
	renderRow,
	forceShowSector = false,
	sectorOptions: sectorOptionsProp,
	sectorFilter: sectorFilterProp,
	onSectorFilterChange,
}) => {
	const isProfileView = mode === "profile-view";
	const isConsultant = profileTypeId === PROFILE_TYPE_CONSULTANT;
	const serverSectorFilter =
		sectorOptionsProp !== undefined &&
		sectorFilterProp !== undefined &&
		onSectorFilterChange !== undefined;
	const [sortField, setSortField] = useState<KeyPersonnelSortField | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const [localSectorFilter, setLocalSectorFilter] = useState<number[]>([]);
	const [expertiseFilter, setExpertiseFilter] = useState<number[]>([]);
	const [disciplineFilter, setDisciplineFilter] = useState<number[]>([]);

	const sectorFilter = serverSectorFilter ? sectorFilterProp : localSectorFilter;
	const setSectorFilter = serverSectorFilter ? onSectorFilterChange : setLocalSectorFilter;

	const handleSort = useCallback(
		(field: KeyPersonnelSortField) => {
			if (sortField === field) {
				setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
			} else {
				setSortField(field);
				setSortDirection("asc");
			}
		},
		[sortField],
	);

	const derivedSectorOptions = useMemo<FilterOption<number>[]>(() => {
		const sectorMap = new Map<number, string>();
		personnel.forEach((p) => {
			p.sectors.forEach((s) => {
				if (!sectorMap.has(s.id)) {
					sectorMap.set(s.id, s.name);
				}
			});
		});
		return Array.from(sectorMap.entries())
			.map(([id, name]) => ({ value: id, label: name }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [personnel]);

	const sectorOptions = sectorOptionsProp ?? derivedSectorOptions;

	const expertiseOptions = useMemo<FilterOption<number>[]>(() => {
		if (!isConsultant) return [];
		const map = new Map<number, string>();
		personnel.forEach((p) => {
			p.disciplines?.forEach((d) => {
				if (d.expertise && !map.has(d.expertise.id)) {
					map.set(d.expertise.id, d.expertise.name);
				}
			});
		});
		return Array.from(map.entries())
			.map(([id, name]) => ({ value: id, label: name }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [personnel, isConsultant]);

	const disciplineOptions = useMemo<FilterOption<number>[]>(() => {
		if (!isConsultant) return [];
		const map = new Map<number, string>();
		personnel.forEach((p) => {
			p.disciplines?.forEach((d) => {
				if (!map.has(d.id)) {
					map.set(d.id, d.name);
				}
			});
		});
		return Array.from(map.entries())
			.map(([id, name]) => ({ value: id, label: name }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [personnel, isConsultant]);

	const sortedAndFiltered = useMemo(() => {
		let result = [...personnel];

		// When sector filter is server-driven, `personnel` is already filtered.
		if (!serverSectorFilter && sectorFilter.length > 0) {
			result = result.filter((p) =>
				p.sectors.some((s) => sectorFilter.includes(s.id)),
			);
		}

		if (isConsultant && expertiseFilter.length > 0) {
			result = result.filter((p) =>
				p.disciplines?.some((d) =>
					d.expertise && expertiseFilter.includes(d.expertise.id),
				),
			);
		}

		if (isConsultant && disciplineFilter.length > 0) {
			result = result.filter((p) =>
				p.disciplines?.some((d) => disciplineFilter.includes(d.id)),
			);
		}

		if (sortField) {
			result.sort((a, b) => {
				let comparison = 0;
				switch (sortField) {
					case "full_name":
						comparison = a.full_name.localeCompare(b.full_name);
						break;
					case "position":
						comparison = a.position.localeCompare(b.position);
						break;
					case "experience":
						comparison = a.experience - b.experience;
						break;
					case "projects":
						comparison =
							(a.projects_completed?.length ?? 0) -
							(b.projects_completed?.length ?? 0);
						break;
					case "resume":
						comparison = a.resume.length - b.resume.length;
						break;
				}
				return sortDirection === "asc" ? comparison : -comparison;
			});
		}

		return result;
	}, [personnel, sortField, sortDirection, sectorFilter, expertiseFilter, disciplineFilter, isConsultant, serverSectorFilter]);

	const handleRowClick = useCallback(
		(person: KeyPersonnelTableItem) => {
			if (onRowClick) {
				onRowClick(person);
			} else if (!isProfileView) {
				onEdit?.(person.id);
			}
		},
		[onRowClick, onEdit, isProfileView],
	);

	const sectorHeader = <th className="px-3 py-3">
		<TableColumnFilter<number>
			label="Top 3 Sectors"
			options={sectorOptions}
			value={sectorFilter}
			onChange={setSectorFilter}
			isMulti
			className="!text-sm !font-medium !text-gray-600"
		/>
	</th>;

	return (
		<div className="overflow-x-auto rounded-lg border border-gray-200">
			<table className="w-full text-left text-sm" data-testid="key-personnel-table">
				<thead className="border-b border-gray-200 bg-gray-50">
					<tr>
						{showDragColumn && <th className="w-10 px-3 py-3" />}
						<th className="w-10 px-3 py-3" />
						<ColumnSortHeader<KeyPersonnelSortField>
							label="Name"
							field="full_name"
							activeField={sortField}
							direction={sortDirection}
							onSort={handleSort}
						/>
						<ColumnSortHeader<KeyPersonnelSortField>
							label="Position"
							field="position"
							activeField={sortField}
							direction={sortDirection}
							onSort={handleSort}
						/>
						<ColumnSortHeader<KeyPersonnelSortField>
							label="Experience"
							field="experience"
							activeField={sortField}
							direction={sortDirection}
							onSort={handleSort}
						/>
						{isConsultant ? (
							<>
								<th className="px-3 py-3">
									<TableColumnFilter<number>
										label="Expertise"
										options={expertiseOptions}
										value={expertiseFilter}
										onChange={setExpertiseFilter}
										isMulti
										className="!text-sm !font-medium !text-gray-600"
									/>
								</th>
								<th className="px-3 py-3">
									<TableColumnFilter<number>
										label="Disciplines"
										options={disciplineOptions}
										value={disciplineFilter}
										onChange={setDisciplineFilter}
										isMulti
										className="!text-sm !font-medium !text-gray-600"
									/>
								</th>
							</>
						) : (
							sectorHeader
						)}
						{isConsultant && forceShowSector && sectorHeader}
						{!isProfileView && (
							<ColumnSortHeader<KeyPersonnelSortField>
								label="Projects"
								field="projects"
								activeField={sortField}
								direction={sortDirection}
								onSort={handleSort}
							/>
						)}
						{isProfileView ? (
							<th className="px-3 py-3 font-medium text-gray-600">
								Resume
							</th>
						) : (
							<ColumnSortHeader<KeyPersonnelSortField>
								label="Resumes"
								field="resume"
								activeField={sortField}
								direction={sortDirection}
								onSort={handleSort}
							/>
						)}
						{!isProfileView && (
							<th className="w-24 px-3 py-3 font-medium text-gray-600">
								Actions
							</th>
						)}
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-100">
					{sortedAndFiltered.map((person) => {
						const sectorCell = <td className="px-3 py-3 text-gray-600">
							{person.sectors.length > 0
								? person.sectors
									.map((s) => s.name)
									.join(", ")
								: "—"}
						</td>;
						const cells = (
							<>
								<td className="px-3 py-3">
									<div
										className="flex items-center justify-center overflow-hidden rounded bg-gray-100"
										style={{ width: 55, height: 55 }}
									>
										{person.avatar_location ? (
											<FallbackImage
												src={person.avatar_location}
												alt={person.full_name}
												className="h-full w-full object-cover"
											/>
										) : (
											<span className="text-sm font-semibold text-gray-400">
												{person.full_name
													.split(" ")
													.map((p) => p.charAt(0))
													.join("")
													.toUpperCase()
													.slice(0, 2)}
											</span>
										)}
									</div>
								</td>
								<td className="px-3 py-3 font-medium text-gray-900">
									{person.full_name}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{person.position}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{person.experience > 0
										? `${person.experience} yrs`
										: "—"}
								</td>
								{isConsultant ? (
									<>
										<td className="px-3 py-3 text-gray-600">
											{person.disciplinesFormatted &&
												person.disciplinesFormatted.length > 0
												? person.disciplinesFormatted
													.map((g) => g.expertise)
													.join(", ")
												: "—"}
										</td>
										<td className="px-3 py-3 text-gray-600">
											{person.disciplines &&
												person.disciplines.length > 0
												? person.disciplines
													.map((d) => d.name)
													.join(", ")
												: "—"}
										</td>
									</>
								) : (
									sectorCell
								)}
								{isConsultant && forceShowSector && sectorCell}
								{!isProfileView && (
									<td className="px-3 py-3 text-gray-600">
										{person.projects_completed?.length ?? 0}
									</td>
								)}
								{isProfileView ? (
									<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
										{person.resume.length > 0 ? (
											<a
												href={person.resume[0].file_path}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700"
												aria-label={`Download resume for ${person.full_name}`}
												data-testid={`resume-download-${person.id}`}
											>
												<FiDownload size={14} />
												Download
											</a>
										) : (
											<span className="text-gray-400">—</span>
										)}
									</td>
								) : (
									<td className="px-3 py-3 text-gray-600">
										{person.resume.length}
									</td>
								)}
								{!isProfileView && (
									<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
										<div className="flex gap-1">
											<button
												type="button"
												className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
												onClick={() => onEdit?.(person.id)}
												aria-label={`Edit ${person.full_name}`}
												data-testid={`edit-btn-${person.id}`}
											>
												<FiEdit2 size={16} />
											</button>
											<button
												type="button"
												className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
												onClick={() => onDelete?.(person.id)}
												aria-label={`Delete ${person.full_name}`}
												data-testid={`delete-btn-${person.id}`}
											>
												<FiTrash2 size={16} />
											</button>
										</div>
									</td>
								)}
							</>
						);

						if (renderRow) {
							return renderRow(person, cells);
						}

						return (
							<tr
								key={person.id}
								className="cursor-pointer hover:bg-blue-50"
								onClick={() => handleRowClick(person)}
								data-testid={`key-personnel-row-${person.id}`}
							>
								{cells}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export { KeyPersonnelTable };
export type { KeyPersonnelTableItem, KeyPersonnelTableProps };
